import { useState } from 'react';

import sanitize from 'klue-html/sanitizer';

type CopiedTextType = string | null;
type CopyToClipboardType = (text: string) => Promise<boolean>;
type CopyRichTextType = (html: string, plainText: string) => Promise<boolean>;

function useCopyToClipboard(): [
  CopyToClipboardType,
  CopiedTextType,
  CopyRichTextType,
] {
  const [copiedText, setCopiedText] = useState<CopiedTextType>(null);

  const copyToClipboard = async (str: string) => {
    if (!navigator?.clipboard) return false;

    try {
      await navigator.clipboard.writeText(str);
      setCopiedText(str);
      return true;
    } catch (error) {
      setCopiedText(null);
      return false;
    }
  };

  const copyRichText = async (
    html: string,
    plainText: string,
  ): Promise<boolean> => {
    // Callers assemble this markup themselves, and the fallback below assigns
    // it to innerHTML on an attached node. Treat it as untrusted.
    const safeHtml = sanitize(html);

    // Try modern Clipboard API first
    if (navigator.clipboard && window.ClipboardItem) {
      try {
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([safeHtml], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        });

        await navigator.clipboard.write([clipboardItem]);
        setCopiedText(plainText);
        return true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(
          'Modern clipboard API failed, falling back to legacy method:',
          error,
        );
      }
    }

    // Fallback to legacy method for older browsers
    const tempDiv = document.createElement('div');
    try {
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.innerHTML = safeHtml;
      document.body.appendChild(tempDiv);

      const range = document.createRange();
      range.selectNodeContents(tempDiv);
      const selection = window.getSelection();

      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);

        const success = document.execCommand('copy');
        selection.removeAllRanges();

        if (success) {
          setCopiedText(plainText);
          return true;
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Fallback clipboard copy failed:', error);
    } finally {
      // Ensure cleanup always happens
      if (tempDiv.parentNode) {
        document.body.removeChild(tempDiv);
      }
    }

    // Final fallback to plain text only
    return copyToClipboard(plainText);
  };

  return [copyToClipboard, copiedText, copyRichText];
}

export default useCopyToClipboard;
